import React, { useState, useEffect } from 'react';
import { Search, Bot, Loader2, AlertCircle, FileText, RefreshCw, Trash2, Upload, FileSearch } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import * as PDFJS from 'pdfjs-dist';

// Initialize PDF.js worker
PDFJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS.version}/pdf.worker.min.js`;

interface Assistant {
  id: string;
  nombre: string;
  descripcion: string;
  api_configuracion_id: string;
  vector_store_id?: string;
}

interface StorageFile {
  name: string;
  id: string;
  updated_at: string;
  created_at: string;
  last_accessed_at: string;
  metadata: {
    size?: number;
    mimetype?: string;
  } | null;
}

export default function PDFFiles() {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [selectedAssistant, setSelectedAssistant] = useState<string>('');
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchAssistants();
  }, []);

  useEffect(() => {
    if (selectedAssistant) {
      fetchFiles();
    } else {
      setFiles([]);
      setSignedUrls({});
    }
  }, [selectedAssistant]);

  const fetchAssistants = async () => {
    try {
      const { data, error } = await supabase
        .from('asistentes')
        .select('*')
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setAssistants(data || []);
    } catch (error: any) {
      console.error('Error fetching assistants:', error);
      setError('Error al cargar los asistentes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFiles = async () => {
    try {
      console.log('Fetching files from storage...');
      const { data: storageData, error: storageError } = await supabase
        .storage
        .from('pdfs')
        .list(selectedAssistant || '', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (storageError) {
        console.error('Storage error:', storageError);
        throw storageError;
      }

      console.log('Files fetched:', storageData?.length || 0, 'files found');

      // Get signed URLs for all files
      const newSignedUrls: Record<string, string> = {};
      for (const file of storageData || []) {
        const filePath = selectedAssistant ? `${selectedAssistant}/${file.name}` : file.name;
        console.log('Getting signed URL for:', filePath);
        
        const { data: { signedUrl }, error: signedUrlError } = await supabase
          .storage
          .from('pdfs')
          .createSignedUrl(filePath, 3600); // 1 hour expiration

        if (signedUrlError) {
          console.error('Error getting signed URL for', filePath, ':', signedUrlError);
          continue;
        }

        if (signedUrl) {
          console.log('Signed URL created for:', filePath);
          newSignedUrls[file.name] = signedUrl;
        }
      }

      setSignedUrls(newSignedUrls);
      setFiles(storageData || []);
      console.log('File list updated successfully');
    } catch (error: any) {
      console.error('Error fetching files:', error);
      setError('Error al cargar los archivos');
    }
  };

  const extractTextFromPDF = async (file: StorageFile): Promise<string> => {
    try {
      console.log('Starting text extraction for:', file.name);
      
      // Get file path
      const filePath = selectedAssistant ? `${selectedAssistant}/${file.name}` : file.name;
      console.log('File path:', filePath);

      // Download file using Supabase storage download
      console.log('Downloading file from storage...');
      const { data: fileData, error: downloadError } = await supabase
        .storage
        .from('pdfs')
        .download(filePath);

      if (downloadError) {
        console.error('Download error:', downloadError);
        throw downloadError;
      }
      if (!fileData) {
        console.error('No file data received');
        throw new Error('No se pudo descargar el archivo');
      }

      console.log('File downloaded successfully, size:', fileData.size, 'bytes');

      // Convert blob to array buffer
      console.log('Converting blob to array buffer...');
      const arrayBuffer = await fileData.arrayBuffer();
      console.log('Array buffer created, size:', arrayBuffer.byteLength, 'bytes');

      // Load the PDF document
      console.log('Loading PDF document...');
      const loadingTask = PDFJS.getDocument({ data: new Uint8Array(arrayBuffer) });
      const pdf = await loadingTask.promise;
      
      console.log(`PDF loaded successfully. Pages: ${pdf.numPages}`);
      
      let fullText = '';

      // Extract text from each page
      for (let i = 1; i <= pdf.numPages; i++) {
        console.log(`Processing page ${i} of ${pdf.numPages}...`);
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        
        fullText += pageText + '\n\n';
        console.log(`Extracted ${pageText.length} characters from page ${i}`);
      }

      if (!fullText.trim()) {
        console.error('No text content found in PDF');
        throw new Error('No se pudo extraer texto del PDF');
      }

      console.log(`Total text extracted: ${fullText.length} characters`);
      return fullText.trim();
    } catch (error: any) {
      console.error('Error extracting text from PDF:', error);
      throw new Error(`Error al extraer texto del PDF: ${error.message}`);
    }
  };

  const handleExtractText = async () => {
    if (selectedFiles.size === 0) {
      setError('Selecciona al menos un archivo para procesar');
      return;
    }

    if (!selectedAssistant) {
      setError('Selecciona un asistente para procesar los archivos');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');
      let processedCount = 0;
      let errorCount = 0;

      const selectedDocs = files.filter(file => selectedFiles.has(file.id));
      console.log('Processing', selectedDocs.length, 'files');

      for (const file of selectedDocs) {
        try {
          console.log('Processing file:', file.name);
          
          // Extract text from PDF
          console.log('Starting text extraction...');
          const extractedText = await extractTextFromPDF(file);
          console.log(`Text extracted successfully from ${file.name}`);

          // Create pdf_extract record
          console.log('Creating pdf_extract record...');
          const { data: extractData, error: extractError } = await supabase
            .from('pdf_extract')
            .insert({
              file_name: file.name,
              file_size: file.metadata?.size || 0,
              file_type: file.metadata?.mimetype || 'application/pdf',
              asistente_id: selectedAssistant,
              estado: 'completado'
            })
            .select()
            .single();

          if (extractError) {
            console.error('Error creating pdf_extract record:', extractError);
            throw extractError;
          }
          console.log('pdf_extract record created:', extractData.id);

          // Create tavily_extract_results record
          console.log('Creating tavily_extract_results record...');
          const { error: tavilyError } = await supabase
            .from('tavily_extract_results')
            .insert({
              url: file.name,
              texto_extraido: extractedText,
              asistente_id: selectedAssistant,
              pdf_extract_id: extractData.id,
              chunk: false,
              embedding: false,
              vectorizado: false
            });

          if (tavilyError) {
            console.error('Error creating tavily_extract_results record:', tavilyError);
            throw tavilyError;
          }
          console.log('tavily_extract_results record created');

          processedCount++;
          console.log(`File ${file.name} processed successfully`);
        } catch (error: any) {
          console.error(`Error processing file ${file.name}:`, error);
          errorCount++;

          // Create error record
          console.log('Creating error record...');
          await supabase
            .from('pdf_extract')
            .insert({
              file_name: file.name,
              file_size: file.metadata?.size || 0,
              file_type: file.metadata?.mimetype || 'application/pdf',
              asistente_id: selectedAssistant,
              estado: 'error',
              error: error.message
            });
        }
      }

      setSuccess(`Proceso completado. ${processedCount} archivos procesados. ${errorCount} errores.`);
      setSelectedFiles(new Set());
    } catch (error: any) {
      console.error('Error extracting text:', error);
      setError(`Error al extraer texto: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    const invalidFiles = files.filter(file => !file.type.includes('pdf'));
    if (invalidFiles.length > 0) {
      setError('Solo se permiten archivos PDF');
      return;
    }

    const largeFiles = files.filter(file => file.size > 20 * 1024 * 1024);
    if (largeFiles.length > 0) {
      setError('Los archivos no deben superar los 20MB cada uno');
      return;
    }

    try {
      setIsUploading(true);
      setError('');

      console.log('Starting file upload process...');
      for (const file of files) {
        // Generate a unique file path
        const timestamp = Date.now();
        const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filePath = selectedAssistant ? `${selectedAssistant}/${timestamp}_${safeFileName}` : `${timestamp}_${safeFileName}`;

        console.log('Uploading file:', filePath);

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('pdfs')
          .upload(filePath, file, {
            contentType: 'application/pdf',
            cacheControl: '3600'
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw uploadError;
        }

        console.log('File uploaded successfully:', filePath);
      }

      setSuccess(`${files.length} archivo(s) subido(s) correctamente`);
      console.log('All files uploaded successfully');
      fetchFiles();
    } catch (error: any) {
      console.error('Error uploading files:', error);
      setError(`Error al subir los archivos: ${error.message}`);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (selectedFiles.size === 0) {
      setError('Selecciona al menos un archivo para eliminar');
      return;
    }

    if (!confirm(`¿Estás seguro de eliminar ${selectedFiles.size} archivo(s)?`)) {
      return;
    }

    try {
      console.log('Starting file deletion process...');
      const filesToDelete = files.filter(file => selectedFiles.has(file.id));
      const filePaths = filesToDelete.map(file => 
        selectedAssistant ? `${selectedAssistant}/${file.name}` : file.name
      );

      console.log('Files to delete:', filePaths);

      const { error } = await supabase.storage
        .from('pdfs')
        .remove(filePaths);

      if (error) {
        console.error('Deletion error:', error);
        throw error;
      }

      console.log('Files deleted successfully');
      setSuccess(`${selectedFiles.size} archivo(s) eliminado(s) correctamente`);
      setSelectedFiles(new Set());
      fetchFiles();
    } catch (error: any) {
      console.error('Error deleting files:', error);
      setError(`Error al eliminar los archivos: ${error.message}`);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes) return 'Desconocido';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileUrl = (fileName: string): string => {
    return signedUrls[fileName] || '#';
  };

  const filteredFiles = files.filter(file => {
    if (searchTerm) {
      return file.name.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-800">Archivos PDF</h1>
          <select
            value={selectedAssistant}
            onChange={(e) => setSelectedAssistant(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Todos los archivos</option>
            {assistants.map(assistant => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <div className="relative">
            <input
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              multiple
              accept=".pdf"
              disabled={isUploading}
            />
            <label
              htmlFor="file-upload"
              className={`px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2 cursor-pointer text-sm ${
                isUploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Subiendo...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Subir PDFs</span>
                </>
              )}
            </label>
          </div>

          <button
            onClick={handleExtractText}
            disabled={selectedFiles.size === 0 || !selectedAssistant || isProcessing}
            className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 text-sm"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <FileSearch className="w-4 h-4" />
                <span>Extraer Texto ({selectedFiles.size})</span>
              </>
            )}
          </button>

          <button
            onClick={handleDelete}
            disabled={selectedFiles.size === 0}
            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2 text-sm"
          >
            <Trash2 className="w-4 h-4" />
            <span>Eliminar ({selectedFiles.size})</span>
          </button>

          <button
            onClick={fetchFiles}
            disabled={isLoading}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Actualizar lista"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar archivos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-medium text-gray-600 w-8">
                    <input
                      type="checkbox"
                      checked={selectedFiles.size === filteredFiles.length && filteredFiles.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFiles(new Set(filteredFiles.map(f => f.id)));
                        } else {
                          setSelectedFiles(new Set());
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={filteredFiles.length === 0}
                    />
                  </th>
                  <th className="pb-3 font-medium text-gray-600">Nombre</th>
                  <th className="pb-3 font-medium text-gray-600">Tamaño</th>
                  <th className="pb-3 font-medium text-gray-600">Tipo</th>
                  <th className="pb-3 font-medium text-gray-600">Subido</th>
                  <th className="pb-3 font-medium text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredFiles.map((file) => (
                  <tr key={file.id} className="border-b">
                    <td className="py-2">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.id)}
                        onChange={() => toggleFileSelection(file.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="py-2">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-blue-500 mr-2" />
                        <a
                          href={getFileUrl(file.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 hover:underline"
                        >
                          {file.name}
                        </a>
                      </div>
                    </td>
                    <td className="py-2">{formatFileSize(file.metadata?.size)}</td>
                    <td className="py-2">{file.metadata?.mimetype || 'application/pdf'}</td>
                    <td className="py-2">
                      {new Date(file.created_at).toLocaleString()}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => window.open(getFileUrl(file.name), '_blank')}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Ver archivo"
                        >
                          <FileSearch className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredFiles.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No hay archivos PDF</p>
                      {!selectedAssistant && (
                        <p className="text-sm mt-2">Selecciona un asistente para ver sus archivos</p>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}